import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  OrgContext,
  POLISHED_VERSION_ORG_ID,
  type OrgContextValue,
  type ResolvedOrg,
} from '@/contexts/OrgContext';
import { setPlatformTenantImpersonation } from '@/lib/platformReadOnlyBoundary';
import { registerPreSignoutHook } from '@/lib/signoutHooks';
import { classifyHost } from '@/lib/tenantHost';

const POLISHED_ORG: ResolvedOrg = {
  id: POLISHED_VERSION_ORG_ID,
  name: 'The Polished Version',
  slug: 'the-polished-version',
};

function getFallbackOrgId(): string {
  return POLISHED_VERSION_ORG_ID;
}

/**
 * Tenant slug from the hostname, if it's one of our subdomains
 * (<slug>.thepolishedversion.com, or <slug>.*.localhost in dev/tests).
 * Only hosts under our root domain count — `acmecleaners.co.uk` is a custom
 * domain, not the slug "acmecleaners".
 */
function parseSubdomain(hostname: string): string | null {
  const kind = classifyHost(hostname);
  return kind.kind === 'subdomain' ? kind.slug : null;
}

/** The hostname to look up as a tenant's own domain, if it could be one. */
function customDomainHost(hostname: string): string | null {
  const kind = classifyHost(hostname);
  return kind.kind === 'custom' ? kind.host : null;
}

/** Admin preview pages that render the signed-in business's site. */
const PREVIEW_FRAME_PATHS = ['/admin/editor-frame', '/admin/theme-preview-frame'];

export const OrgProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isPlatformAdmin, organizationMembership, rolesLoading } = useAuth();
  const subdomain = useMemo(() => parseSubdomain(window.location.hostname), []);

  const [realOrg, setRealOrg] = useState<ResolvedOrg>(POLISHED_ORG);
  const [impersonatedOrg, setImpersonatedOrg] = useState<ResolvedOrg | null>(null);
  const [impersonationStartedAt, setImpersonationStartedAt] = useState<number | null>(null);
  // Authoritative session identity from the server: the id lets end/expire close
  // the EXACT row this browser holds, and expires_at drives the countdown/timer
  // (the browser no longer assumes a fixed one-hour window).
  const [impersonationSessionId, setImpersonationSessionId] = useState<string | null>(null);
  const [impersonationExpiresAt, setImpersonationExpiresAt] = useState<number | null>(null);
  const [isCustomDomainOrg, setIsCustomDomainOrg] = useState(false);
  const [loading, setLoading] = useState<boolean>(
    Boolean(subdomain) || Boolean(customDomainHost(window.location.hostname))
  );
  const [isUnknownSubdomain, setIsUnknownSubdomain] = useState(false);
  const [isSuspended, setIsSuspended] = useState(false);

  // Marketing site = apex/preview root with no tenant subdomain.
  // Logged-in admins should still see the sales homepage at "/"; admin context lives under /admin.
  // The admin's in-page preview frames (page editor, theme previews) show the
  // signed-in business's own site, whatever host the admin is on. Without this
  // the apex/preview host resolves to the Polished Version fallback and the
  // preview wears our header, footer and theme. Scoped to these paths only: the
  // public /demo route must stay anchored to Polished Version.
  const isAdminPreviewFrame = PREVIEW_FRAME_PATHS.some((p) => window.location.pathname.startsWith(p));
  const previewOrg: ResolvedOrg | null =
    isAdminPreviewFrame && !impersonatedOrg && organizationMembership && realOrg.id !== organizationMembership.organizationId
      ? { id: organizationMembership.organizationId, name: organizationMembership.organizationName, slug: '' }
      : null;
  const isMarketingSite = subdomain === null && !isCustomDomainOrg && !impersonatedOrg && !isAdminPreviewFrame;
  const org = impersonatedOrg ?? previewOrg ?? realOrg;

  useEffect(() => {
    let cancelled = false;

    async function checkSuspended(orgId: string) {
      const { data } = await supabase.rpc('is_org_suspended' as never, { _org_id: orgId } as never);
      if (!cancelled) setIsSuspended(Boolean(data));
    }

    async function resolve() {
      setIsSuspended(false);
      // 1. If a subdomain is present, look up that org by slug.
      if (subdomain) {
        setLoading(true);
        setIsCustomDomainOrg(false);
        const { data, error } = await supabase.rpc('get_org_by_slug', { _slug: subdomain });

        if (cancelled) return;

        if (error) {
          setIsUnknownSubdomain(true);
          setLoading(false);
          console.error('Failed to resolve tenant subdomain', {
            subdomain,
            hostname: window.location.hostname,
            error,
          });
          toast.error(`Could not load tenant "${subdomain}" — ${error.message}`);
          return;
        }

        if (!data || data.length === 0) {
          setIsUnknownSubdomain(true);
          setLoading(false);
          console.warn('Unknown tenant subdomain', { subdomain, hostname: window.location.hostname });
          return;
        }

        const row = data[0];
        setRealOrg({
          id: row.id,
          name: row.name,
          slug: row.slug,
          logoUrl: row.logo_url,
          tagline: row.tagline,
          description: row.description,
          coverImageUrl: row.cover_image_url,
        });
        await checkSuspended(row.id);
        setLoading(false);
        return;
      }

      // 2. No subdomain — check if the hostname is a verified custom domain
      //    (get_org_by_domain matches both the apex and www.).
      const hostname = customDomainHost(window.location.hostname);
      if (hostname) {
        const { data } = await supabase.rpc('get_org_by_domain' as never, { _domain: hostname } as never);

        if (cancelled) return;

        type DomainOrgRow = {
          id: string; name: string; slug: string;
          logo_url: string | null; tagline: string | null; description: string | null; cover_image_url: string | null;
        };
        const domainRows = (Array.isArray(data) ? data : []) as DomainOrgRow[];
        if (domainRows.length > 0) {
          const row = domainRows[0];
          setIsCustomDomainOrg(true);
          setRealOrg({
            id: row.id,
            name: row.name,
            slug: row.slug,
            logoUrl: row.logo_url,
            tagline: row.tagline,
            description: row.description,
            coverImageUrl: row.cover_image_url,
          });
          await checkSuspended(row.id);
          setLoading(false);
          return;
        }
      }

      // 3. Apex / preview / localhost fallback → Polished Version (demo tenant).
      //    Logged-in owners see their own data via their tenant subdomain;
      //    /demo here must stay anchored to the Polished Version regardless of who's signed in.
      setIsCustomDomainOrg(false);
      setRealOrg({ ...POLISHED_ORG, id: getFallbackOrgId() });
      setLoading(false);
    }

    resolve();
    return () => {
      cancelled = true;
    };
  }, [subdomain]);

  const clearImpersonationState = () => {
    setImpersonatedOrg(null);
    setImpersonationStartedAt(null);
    setImpersonationSessionId(null);
    setImpersonationExpiresAt(null);
    setPlatformTenantImpersonation(false);
  };

  // Auto-expire impersonation at the SERVER's expires_at (not a fixed local
  // hour). Expiry is an unavoidable recovery path, so local state is cleared
  // regardless, but the server is still asked to close and AUDIT the expiry
  // authoritatively (support_session_expire) for the EXACT session id — a stale
  // timer for a superseded session can only ever no-op server-side.
  useEffect(() => {
    if (!impersonationExpiresAt || !impersonationSessionId) return;
    const expiringOrgId = impersonatedOrg?.id ?? null;
    const expiringSessionId = impersonationSessionId;

    const expire = () => {
      clearImpersonationState();
      // best-effort: expiry recovery — the server session also self-expires.
      // Promise.resolve() because the rpc builder is a thenable without .catch.
      void Promise.resolve(
        supabase.rpc('expire_tenant_view_session' as never, {
          _session_id: expiringSessionId,
          _org_id: expiringOrgId,
        } as never),
      ).catch(() => undefined);
    };

    const expiresIn = impersonationExpiresAt - Date.now();
    if (expiresIn <= 0) {
      expire();
      return;
    }

    const timer = setTimeout(expire, expiresIn);
    return () => clearTimeout(timer);
  }, [impersonationExpiresAt, impersonationSessionId, impersonatedOrg?.id]);

  // Impersonation belongs to the active platform-admin session only. On logout or
  // role loss, close the server session (by exact session id) as far as the
  // (possibly gone) auth session still permits, then clear both React state and
  // the module-level network boundary so a later tenant owner session is never
  // left read-only.
  useEffect(() => {
    if (!impersonatedOrg || (user && isPlatformAdmin)) return;
    const orgId = impersonatedOrg.id;
    const sessionId = impersonationSessionId;
    if (sessionId) {
      // best-effort: the auth session may already be gone; server session self-expires.
      // Promise.resolve() because the rpc builder is a thenable without .catch.
      void Promise.resolve(
        supabase.rpc('end_tenant_view_session' as never, {
          _session_id: sessionId,
          _org_id: orgId,
        } as never),
      ).catch(() => undefined);
    }
    clearImpersonationState();
  }, [impersonatedOrg, isPlatformAdmin, user, impersonationSessionId]);

  // Close the authoritative server session on explicit sign-out BEFORE Supabase
  // clears the auth token, via the shared pre-sign-out registry (AuthProvider
  // runs these while the token is still valid). Errors are surfaced, never
  // treated as a successful closure.
  useEffect(() => {
    if (!impersonatedOrg || !impersonationSessionId) return;
    const sessionId = impersonationSessionId;
    const orgId = impersonatedOrg.id;
    return registerPreSignoutHook(async () => {
      const { error } = await supabase.rpc('end_tenant_view_session' as never, {
        _session_id: sessionId,
        _org_id: orgId,
      } as never);
      if (error) {
        // Do not claim closure: report it. The server session also self-expires.
        console.error('Failed to close support session before sign-out', error);
      }
    });
  }, [impersonatedOrg, impersonationSessionId]);

  useEffect(() => () => setPlatformTenantImpersonation(false), []);

  // User-initiated stop. Unlike expiry/logout recovery, this MUST inspect the
  // server result: we never silently claim closure after a server failure. On
  // error we surface it and keep the session (and boundary) active so the admin
  // can retry, rather than leaving a live server session the UI believes is shut.
  const stopImpersonation = async () => {
    const targetOrg = impersonatedOrg;
    const sessionId = impersonationSessionId;
    if (!targetOrg || !sessionId) {
      clearImpersonationState();
      return;
    }
    const { error } = await supabase.rpc(
      'end_tenant_view_session' as never,
      { _session_id: sessionId, _org_id: targetOrg.id } as never,
    );
    if (error) {
      throw new Error(error.message || 'Could not end the support session. Please try again.');
    }
    clearImpersonationState();
  };

  const startImpersonation = async (targetOrgId: string, reason: string, caseRef?: string) => {
    if (!isPlatformAdmin) throw new Error('Only platform admins can impersonate tenants');
    if (!reason || reason.trim().length < 8) {
      throw new Error('A support reason of at least 8 characters is required to view a tenant.');
    }

    // The server validates the reason, records the authoritative view-session
    // row and audits it in one transaction, and returns the tenant to display.
    // No client-fabricated reason and no standalone browser audit assertion.
    const { data, error } = await supabase.rpc('start_tenant_view_session' as never, {
      _org_id: targetOrgId,
      _reason: reason.trim(),
      _case_ref: caseRef?.trim() || null,
    } as never);

    if (error) throw new Error(error.message || 'Could not start support session');

    const session = (data ?? {}) as {
      session_id?: string;
      started_at?: string;
      expires_at?: string;
      organization?: {
        id: string;
        name: string;
        slug: string;
        logo_url: string | null;
        tagline: string | null;
        description: string | null;
        cover_image_url: string | null;
      };
    };
    const orgRow = session.organization;
    if (!orgRow) throw new Error('Support session did not return the tenant');
    if (!session.session_id) throw new Error('Support session did not return a session id');

    const startedMs = session.started_at ? Date.parse(session.started_at) : Date.now();
    const expiresMs = session.expires_at ? Date.parse(session.expires_at) : Date.now() + 60 * 60 * 1000;

    setImpersonatedOrg({
      id: orgRow.id,
      name: orgRow.name,
      slug: orgRow.slug,
      logoUrl: orgRow.logo_url,
      tagline: orgRow.tagline,
      description: orgRow.description,
      coverImageUrl: orgRow.cover_image_url,
    });
    setImpersonationSessionId(session.session_id);
    setImpersonationStartedAt(Number.isFinite(startedMs) ? startedMs : Date.now());
    setImpersonationExpiresAt(Number.isFinite(expiresMs) ? expiresMs : Date.now() + 60 * 60 * 1000);
    // Activate the read-only boundary only after the server session is live.
    setPlatformTenantImpersonation(true);
  };

  const value: OrgContextValue = {
    org,
    orgId: org.id,
    realOrg,
    // Hold preview frames until we know whose business it is, so they never
    // flash the fallback org's header first.
    loading: loading || (isAdminPreviewFrame && rolesLoading),
    isMarketingSite,
    isUnknownSubdomain,
    isSuspended,
    subdomain,
    isImpersonating: Boolean(impersonatedOrg),
    impersonationStartedAt,
    impersonationExpiresAt,
    canWrite: !impersonatedOrg,
    startImpersonation,
    stopImpersonation,
  };

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
};
